import * as React from "react"
import { SvgCalendar } from "@digits-shared/components/SVGIcons/line/Calendar.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { Menu, MenuContent, MenuItem, MenuTrigger } from "@digits-shared/DesignSystem/Menu"
import ElementArrayHelper from "@digits-shared/helpers/ElementArrayHelper"
import useRouter from "@digits-shared/hooks/useRouter"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import useSearchContext from "src/frontend/components/OS/Applications/Search/SearchContext"
import { DateTitle } from "src/frontend/components/OS/Applications/Search/SearchElements"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"

enum DateRange {
  AllTime = "All time",
  Last30Days = "Last 30 days",
  ThisMonth = "This month",
  LastMonth = "Last month",
  ThisQuarter = "This quarter",
  LastQuarter = "Last quarter",
  ThisYear = "This year",
  LastYear = "Last year",
}

/*
  STYLES
*/

/*
  INTERFACES
*/

interface Props {
  className?: string
}

interface DateRangesProps {
  currentRange: DateRange
  onRangeSelected: (range: DateRange, occurredAfter?: Date, occurredBefore?: Date) => void
}

/*
  COMPONENTS
*/

const DatePicker: React.FC<Props> = ({ className }) => {
  const generatePath = useFrontendPathGenerator()
  const { builder, query } = useSearchContext()
  const { history } = useRouter()
  const [range, setRange] = React.useState<DateRange>(DateRange.AllTime)

  const occurredAfter = query.occurredAfterDate
  const occurredBefore = query.occurredBeforeDate

  const onDatePicked = React.useCallback(
    (newRange: DateRange, newOccurredAfter?: Date, newOccurredBefore?: Date) => {
      setRange(newRange)

      const urlParams = builder
        .setOccurredAfter(newOccurredAfter)
        .setOccurredBefore(newOccurredBefore)
        .buildUrlParams()

      const path = generatePath(routes.search, urlParams)
      history.push(path)
    },
    [builder, history, generatePath]
  )

  return (
    <Menu>
      <MenuTrigger asChild>
        <DigitsButton $variant="secondary-dark" size="small">
          <SvgCalendar />
          <DateTitle occurredAfter={occurredAfter} occurredBefore={occurredBefore} />
        </DigitsButton>
      </MenuTrigger>

      <DateRanges currentRange={range} onRangeSelected={onDatePicked} />
    </Menu>
  )
}

export default DatePicker

const DateRanges: React.FC<DateRangesProps> = ({ currentRange, onRangeSelected }) => {
  const onRangeClick = React.useCallback(
    (range: DateRange) => {
      const utc = dayjs().utc()

      let occurredAfter: Date | undefined
      let occurredBefore: Date | undefined
      switch (range) {
        case DateRange.AllTime:
          occurredAfter = undefined
          occurredBefore = undefined
          break

        case DateRange.Last30Days:
          occurredAfter = utc.subtract(30, "days").toDate()
          occurredBefore = undefined
          break

        case DateRange.ThisMonth:
          occurredAfter = utc.startOf("month").toDate()
          occurredBefore = undefined
          break
        case DateRange.LastMonth:
          occurredAfter = utc.subtract(1, "months").startOf("month").toDate()
          occurredBefore = utc.endOf("month").toDate()
          break

        case DateRange.ThisQuarter:
          occurredAfter = utc.startOf("quarter").toDate()
          occurredBefore = undefined
          break
        case DateRange.LastQuarter:
          occurredAfter = utc.subtract(1, "quarter").startOf("quarter").toDate()
          occurredBefore = utc.endOf("quarter").toDate()
          break

        case DateRange.ThisYear:
          occurredAfter = utc.startOf("year").toDate()
          occurredBefore = undefined
          break
        case DateRange.LastYear:
          occurredAfter = utc.subtract(1, "year").startOf("year").toDate()
          occurredBefore = utc.endOf("year").toDate()
          break
      }

      onRangeSelected(range, occurredAfter, occurredBefore)
    },
    [onRangeSelected]
  )

  const ranges = [
    [DateRange.AllTime],
    [DateRange.Last30Days],
    [DateRange.ThisMonth, DateRange.LastMonth],
    [DateRange.ThisQuarter, DateRange.LastQuarter],
    [DateRange.ThisYear, DateRange.LastYear],
  ]

  const items = ranges.map((section) =>
    section.map((range, index) => (
      <MenuItem
        onSelect={onRangeClick.bind(undefined, range)}
        label={range}
        key={range}
        active={range === currentRange}
      />
    ))
  )

  return <MenuContent small>{new ElementArrayHelper(items)}</MenuContent>
}
